import { cn } from "@journal/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark. Default "h1". Pass "p" when the logo is
   * decorative and another element already owns the page heading (e.g. the auth
   * card carries the sole page h1 → the brand surface renders the wordmark as a
   * non-heading <p>).
   */
  as?: "h1" | "p";
}

/**
 * Daily Journal mark — an open notebook with a pen stroke that
 * "signs" the page, its own distinctive glyph (not the generic template one).
 *
 * Contrast is guaranteed in ANY theme (including low-contrast custom accents):
 * the glyph sits on a SOLID `bg-accent` chip and is drawn with
 * `text-accent-foreground` (the pair with guaranteed contrast). The wordmark uses
 * `text-foreground` and never depends on the accent. This avoids the bug that broke
 * GymLog (mark in text-accent on a faint surface → invisible).
 */
function JournalMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      class={props.class}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Open notebook: two mirrored pages with a central spine */}
      <path
        d="M16 9.4C13.6 7.8 10.6 7.1 7.4 7.4c-.8.1-1.4.8-1.4 1.6v12.7c0 .9.8 1.6 1.7 1.5 2.7-.3 5.4.3 7.6 1.7.4.3 1 .3 1.4 0 2.2-1.4 4.9-2 7.6-1.7.9.1 1.7-.6 1.7-1.5V9c0-.8-.6-1.5-1.4-1.6-3.2-.3-6.2.4-8.6 2z"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linejoin="round"
        stroke-linecap="round"
      />
      {/* Lomo central */}
      <path d="M16 9.4v15.3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
      {/* Writing lines on the left page */}
      <path
        d="M9.6 12.6c1.4-.1 2.7.1 3.9.6M9.6 15.6c1.4-.1 2.7.1 3.9.6"
        stroke="currentColor"
        stroke-width="1.3"
        stroke-linecap="round"
        opacity="0.75"
      />
      {/* Pen signing the right page — distinctive stroke */}
      <path
        d="M22.6 12.2c-.4 2.6-1.7 4.4-3.9 5.4l-.7-.7c1-2.2 2.8-3.5 4.6-4.7z"
        fill="currentColor"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkClass = "font-display text-4xl font-semibold tracking-tight text-foreground";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div
            data-testid="brand-mark"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5"
          >
            <JournalMark class="h-[1.35rem] w-[1.35rem]" />
          </div>
          <span class="font-display text-[1.35rem] font-semibold leading-none tracking-tight text-foreground">
            Journal
          </span>
        </>
      ) : (
        <>
          <div
            data-testid="brand-mark"
            class="mb-1 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-md ring-1 ring-black/5 transition-transform duration-300 hover:scale-105 active:scale-95"
          >
            <JournalMark class="h-9 w-9" />
          </div>
          {(props.as ?? "h1") === "p" ? (
            <p class={wordmarkClass}>Journal</p>
          ) : (
            <h1 class={wordmarkClass}>Journal</h1>
          )}
        </>
      )}
    </div>
  );
}
